import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { api, API, fmtBRL, fmtDate, fmtDateOnly, CATEGORIA_LABEL, REFEICAO_LABEL, COMBUSTIVEL_LABEL, fmtUnidade } from "@/lib/api";

const LOGO_URL = "https://customer-assets-39nsmqrw.emergentagent.net/job_motoserra-depot/artifacts/18uda418_logo%20Padr%C3%A3o.png";

export default function Print() {
  const { tipo, id } = useParams();
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchData = async () => {
      try {
        if (tipo === "requisicao") {
          const r = await api.get("/requisitions");
          setData(r.data.find((x) => x.id === id));
        } else if (tipo === "compra-fornecedor") {
          const r = await api.get("/supplier-purchases");
          setData(r.data.find((x) => x.id === id));
        } else if (tipo === "compra-fornecedor-lote") {
          const r = await api.get("/supplier-purchases/grouped");
          setData(r.data.find((g) => g.batch_id === id));
        } else if (tipo === "compra-comunidade") {
          const r = await api.get("/community-purchases");
          setData(r.data.find((x) => x.id === id));
        } else if (tipo === "compra-comunidade-lote") {
          const r = await api.get("/community-purchases/grouped");
          setData(r.data.find((g) => g.batch_id === id));
        } else if (tipo === "epi") {
          const r = await api.get("/epi-deliveries");
          setData(r.data.find((x) => x.id === id));
        } else if (tipo === "colaborador") {
          const r = await api.get(`/reports/collaborator/${id}`);
          setData(r.data);
        } else if (tipo === "ticket") {
          const r = await api.get(`/visitor-tickets/${id}`);
          setData(r.data);
        } else if (tipo === "combustivel") {
          const r = await api.get(`/fuel-requests/${id}`);
          setData(r.data);
        }
      } catch (e) { setError(e.response?.data?.detail || "Erro ao carregar"); }
    };
    fetchData();
  }, [tipo, id]);

  useEffect(() => {
    if (data) setTimeout(() => window.print(), 600);
  }, [data]);

  if (error) return <div className="p-8 font-mono-data">Erro: {error}</div>;
  if (!data) return <div className="p-8 font-mono-data">Carregando ficha...</div>;

  return (
    <div className="print-root bg-white min-h-screen p-6 max-w-4xl mx-auto" style={{ fontFamily: "'IBM Plex Sans', sans-serif", color: "#111" }}>
      <style>{`
        @media print {
          @page { size: A4; margin: 10mm; }
          body { background: white !important; }
          .no-print { display: none !important; }
          .print-root { padding: 0 !important; max-width: none !important; }
          #emergent-badge { display: none !important; }
          .premium-header { -webkit-print-color-adjust: exact; print-color-adjust: exact; }
        }
        .ficha-h { font-family: 'Chivo', sans-serif; font-weight: 900; text-transform: uppercase; letter-spacing: -0.02em; }
        .ficha-mono { font-family: 'IBM Plex Mono', monospace; }
        .premium-header { background: linear-gradient(135deg, #0A3C1E 0%, #1E5D3F 60%, #2A7A54 100%); color: white; position: relative; overflow: hidden; }
        .premium-header::before {
          content: ''; position: absolute; inset: 0;
          background: radial-gradient(circle at 85% 20%, rgba(212,160,23,0.18) 0%, transparent 40%),
                      repeating-linear-gradient(45deg, transparent 0 22px, rgba(255,255,255,0.03) 22px 23px);
          pointer-events: none;
        }
      `}</style>

      <div className="no-print mb-4 flex gap-2">
        <button onClick={() => window.print()} className="brutal-btn bg-[#FF4F00] text-white px-4 py-2 font-display font-bold uppercase text-xs tracking-widest">Imprimir</button>
        <button onClick={() => window.close()} className="brutal-btn bg-white text-[#111] px-4 py-2 font-display font-bold uppercase text-xs tracking-widest">Fechar</button>
      </div>

      {/* Cabeçalho institucional premium — padronizado em todas as fichas */}
      <div className="premium-header p-6 mb-6 border-2 border-[#0A3C1E]">
        <div className="relative flex items-center justify-between gap-4">
          <div className="flex items-center gap-4">
            <img src={LOGO_URL} alt="COOMFLONA" className="w-24 h-24 object-contain shrink-0 drop-shadow-xl" />
            <div>
              <div className="ficha-h text-3xl leading-none" style={{ color: "#F3C84B" }}>COOMFLONA</div>
              <div className="ficha-mono text-[10px] uppercase tracking-widest text-white/85 mt-1 font-bold">Cooperativa Mista da Floresta Nacional do Tapajós</div>
              <div className="ficha-mono text-[10px] text-white/70 mt-0.5">CNPJ: 07.315.165/0001-00</div>
              <div className="ficha-mono text-[10px] text-white/70">BR 163 KM 117 · Belterra-PA · CEP 68.143-000</div>
            </div>
          </div>
          <div className="text-right shrink-0">
            <div className="ficha-mono text-[9px] uppercase tracking-widest text-white/70">Empreendimento</div>
            <div className="ficha-mono text-sm font-bold text-[#F3C84B] mb-2 uppercase">{fmtUnidade(data.unidade || data.filial)}</div>
            <div className="ficha-mono text-[9px] uppercase tracking-widest text-white/70">Emitido em</div>
            <div className="ficha-mono text-sm font-bold text-white">{new Date().toLocaleString("pt-BR")}</div>
          </div>
        </div>
      </div>

      {tipo === "requisicao" && <FichaReq req={data} />}
      {tipo === "combustivel" && <FichaCombustivel f={data} />}
      {tipo === "compra-fornecedor" && <FichaCompraFornecedor c={data} />}
      {tipo === "compra-fornecedor-lote" && <FichaCompraFornecedorLote g={data} />}
      {tipo === "compra-comunidade" && <FichaCompraComunidade c={data} />}
      {tipo === "compra-comunidade-lote" && <FichaCompraComunidadeLote g={data} />}
      {tipo === "epi" && <FichaEpi e={data} />}
      {tipo === "colaborador" && <FichaColaborador d={data} />}
      {tipo === "ticket" && <FichaTicket t={data} />}

      <div className="mt-8 pt-4 border-t-2 border-[#0A3C1E] ficha-mono text-[9px] uppercase text-[#555] text-center">
        COOMFLONA · Sistema de Almoxarifado · Documento gerado eletronicamente
      </div>
    </div>
  );
}

const Row = ({ label, value, mono }) => (
  <div>
    <div className="ficha-mono text-[9px] uppercase tracking-widest text-[#555]">{label}</div>
    <div className={`text-sm ${mono ? "ficha-mono" : ""} font-medium`}>{value || "—"}</div>
  </div>
);

const SignBox = ({ label }) => (
  <div className="mt-12">
    <div className="border-t border-[#111] pt-1 ficha-mono text-[10px] uppercase tracking-widest text-[#555]">{label}</div>
  </div>
);

function FichaReq({ req }) {
  const displayNum = req.numero_sequencial || req.numero;
  return (
    <div>
      <div className="flex items-center justify-between mb-4">
        <h1 className="ficha-h text-3xl">Requisição de Material</h1>
        <div className="text-right">
          <div className="ficha-mono text-[10px] uppercase text-[#555]">Número</div>
          <div className="ficha-mono font-bold text-3xl text-[#FF4F00]">Nº {displayNum}</div>
          {req.numero_sequencial && <div className="ficha-mono text-[9px] text-[#777] mt-0.5">ref. {req.numero}</div>}
        </div>
      </div>
      <div className="grid grid-cols-2 gap-3 mb-5 border border-[#111] p-3">
        <Row label="Solicitante" value={req.requester_nome} />
        <Row label="Setor" value={req.sector_nome} />
        <Row label="Criada em" value={fmtDate(req.created_at)} mono />
        <Row label="Aprovador" value={req.aprovador_nome} />
        <Row label="Status" value={req.status} />
        <Row label="Entregue em" value={fmtDate(req.delivered_at)} mono />
      </div>
      {req.motivo && <div className="mb-4"><div className="ficha-mono text-[10px] uppercase text-[#555] mb-1">Motivo</div><div className="border border-[#111] p-2 text-sm">{req.motivo}</div></div>}
      <table className="w-full text-sm border-collapse mb-6">
        <thead><tr className="bg-[#0A1F14] text-white"><th className="text-left p-2 ficha-mono text-[10px] uppercase">Código</th><th className="text-left p-2 ficha-mono text-[10px] uppercase">Item</th><th className="text-right p-2 ficha-mono text-[10px] uppercase">Solicitado</th><th className="text-right p-2 ficha-mono text-[10px] uppercase">Entregue</th></tr></thead>
        <tbody>{req.itens.map((it, i) => (
          <tr key={i} className="border-b border-[#EEE]">
            <td className="p-2 ficha-mono text-xs">{it.codigo}</td>
            <td className="p-2">{it.nome}</td>
            <td className="p-2 text-right ficha-mono">{it.quantidade_solicitada} {it.unidade}</td>
            <td className="p-2 text-right ficha-mono font-bold">{it.quantidade_entregue || 0}</td>
          </tr>
        ))}</tbody>
      </table>
      <div className="grid grid-cols-3 gap-6 mt-10">
        <SignBox label="Solicitante" />
        <SignBox label="Aprovador (Almoxarife)" />
        <SignBox label="Setor de Compras" />
      </div>
    </div>
  );
}

function FichaCompraFornecedor({ c }) {
  return (
    <div>
      <div className="flex items-center justify-between mb-4">
        <h1 className="ficha-h text-3xl">Recibo · Compra Fornecedor</h1>
        <div className="text-right">
          <div className="ficha-mono text-[10px] uppercase text-[#555]">Número</div>
          <div className="ficha-mono font-bold text-2xl">{c.numero}</div>
        </div>
      </div>
      <div className="border border-[#111] p-3 mb-5">
        <div className="ficha-mono text-[10px] uppercase tracking-widest text-[#555]">Fornecedor</div>
        <div className="text-lg font-bold">{c.supplier_nome}</div>
        {c.supplier_cnpj && <div className="ficha-mono text-xs text-[#555]">CNPJ/CPF · <strong className="text-[#111]">{c.supplier_cnpj}</strong></div>}
      </div>
      <table className="w-full text-sm border-collapse mb-5">
        <thead><tr className="bg-[#0A1F14] text-white"><th className="text-left p-2 ficha-mono text-[10px] uppercase">Produto</th><th className="text-right p-2 ficha-mono text-[10px] uppercase">Qtd</th><th className="text-right p-2 ficha-mono text-[10px] uppercase">Vlr Un</th><th className="text-right p-2 ficha-mono text-[10px] uppercase">Total</th></tr></thead>
        <tbody><tr><td className="p-2">{c.product_nome} <span className="ficha-mono text-xs text-[#777]">({c.product_codigo})</span></td><td className="p-2 text-right ficha-mono">{c.quantidade}</td><td className="p-2 text-right ficha-mono">{fmtBRL(c.valor_unitario)}</td><td className="p-2 text-right ficha-mono font-bold">{fmtBRL(c.valor_total)}</td></tr></tbody>
      </table>
      <div className="grid grid-cols-3 gap-3 border-t-2 border-[#111] pt-3 mb-6">
        <Row label="Data" value={fmtDate(c.data_compra)} mono />
        <Row label="Nota Fiscal" value={c.nota_fiscal} mono />
        <Row label="Pagamento" value={c.forma_pagamento} />
        <Row label="Responsável" value={c.responsavel_nome} />
        <div className="col-span-2 text-right">
          <div className="ficha-mono text-[10px] uppercase text-[#FF4F00]">VALOR TOTAL</div>
          <div className="ficha-h text-3xl">{fmtBRL(c.valor_total)}</div>
        </div>
      </div>
      {c.observacoes && <div className="mb-6"><div className="ficha-mono text-[10px] uppercase text-[#555] mb-1">Observações</div><div className="border border-[#111] p-2 text-sm">{c.observacoes}</div></div>}
      <div className="grid grid-cols-2 gap-8">
        <SignBox label="Almoxarife (Conferido)" />
        <SignBox label="Fornecedor / Entregador" />
      </div>
    </div>
  );
}

function FichaCompraComunidade({ c }) {
  const isCom = (c.cooperado_tipo || "cooperado") === "comunitario";
  return (
    <div>
      <div className="flex items-center justify-between mb-4">
        <h1 className="ficha-h text-3xl">Recibo · {isCom ? "Compra Comunitária" : "Compra de Cooperado"}</h1>
        <div className="text-right">
          <div className="ficha-mono text-[10px] uppercase text-[#555]">Número</div>
          <div className="ficha-mono font-bold text-2xl">{c.numero}</div>
        </div>
      </div>
      <div className="border border-[#111] p-3 mb-5">
        <div className="flex items-center justify-between mb-1">
          <div className="ficha-mono text-[10px] uppercase tracking-widest text-[#555]">{isCom ? "Comunitário" : "Cooperado"}</div>
          <div className={`px-2 py-0.5 text-white ficha-mono text-[10px] uppercase tracking-widest font-bold ${isCom ? "bg-[#FF4F00]" : "bg-[#3D6B57]"}`}>
            {isCom ? "Comunitário" : "Cooperado"}
          </div>
        </div>
        <div className="text-lg font-bold">{c.cooperado_nome}</div>
        <div className="flex flex-wrap gap-x-4 gap-y-0.5 mt-1">
          {c.cooperado_cpf && <span className="ficha-mono text-xs text-[#555]">CPF · <strong className="text-[#111]">{c.cooperado_cpf}</strong></span>}
          {c.community_nome && <span className="ficha-mono text-xs text-[#555]">Comunidade · {c.community_nome}</span>}
        </div>
      </div>
      <table className="w-full text-sm border-collapse mb-5">
        <thead><tr className="bg-[#0A1F14] text-white"><th className="text-left p-2 ficha-mono text-[10px] uppercase">Produto</th><th className="text-right p-2 ficha-mono text-[10px] uppercase">Qtd</th><th className="text-right p-2 ficha-mono text-[10px] uppercase">Vlr Un</th><th className="text-right p-2 ficha-mono text-[10px] uppercase">Total</th></tr></thead>
        <tbody><tr><td className="p-2">{c.product_nome} <span className="ficha-mono text-xs text-[#777]">({c.product_codigo})</span></td><td className="p-2 text-right ficha-mono">{c.quantidade}</td><td className="p-2 text-right ficha-mono">{fmtBRL(c.valor_unitario)}</td><td className="p-2 text-right ficha-mono font-bold">{fmtBRL(c.valor_total)}</td></tr></tbody>
      </table>
      <div className="grid grid-cols-3 gap-3 border-t-2 border-[#111] pt-3 mb-6">
        <Row label="Data" value={fmtDate(c.data_compra)} mono />
        <Row label="Recibo / NF" value={c.nota_fiscal} mono />
        <Row label="Responsável" value={c.responsavel_nome} />
        <div className="col-span-2"></div>
        <div className="text-right">
          <div className="ficha-mono text-[10px] uppercase text-[#FF4F00]">VALOR PAGO</div>
          <div className="ficha-h text-3xl">{fmtBRL(c.valor_total)}</div>
        </div>
      </div>
      {c.observacoes && <div className="mb-6"><div className="ficha-mono text-[10px] uppercase text-[#555] mb-1">Observações</div><div className="border border-[#111] p-2 text-sm">{c.observacoes}</div></div>}
      <div className="grid grid-cols-2 gap-8">
        <SignBox label="Almoxarife COOMFLONA" />
        <SignBox label={`${isCom ? "Comunitário" : "Cooperado"} (Recebi o valor acima)`} />
      </div>
    </div>
  );
}


function FichaCompraComunidadeLote({ g }) {
  const isCom = (g.cooperado_tipo || "cooperado") === "comunitario";
  return (
    <div>
      <div className="flex items-center justify-between mb-4">
        <h1 className="ficha-h text-3xl">Recibo · {isCom ? "Compra Comunitária" : "Compra de Cooperado"}</h1>
        <div className="text-right">
          <div className="ficha-mono text-[10px] uppercase text-[#555]">Número</div>
          <div className="ficha-mono font-bold text-2xl">{g.numero}</div>
        </div>
      </div>
      <div className="border border-[#111] p-3 mb-5">
        <div className="flex items-center justify-between mb-1">
          <div className="ficha-mono text-[10px] uppercase tracking-widest text-[#555]">{isCom ? "Comunitário" : "Cooperado"}</div>
          <div className={`px-2 py-0.5 text-white ficha-mono text-[10px] uppercase tracking-widest font-bold ${isCom ? "bg-[#FF4F00]" : "bg-[#3D6B57]"}`}>
            {isCom ? "Comunitário" : "Cooperado"}
          </div>
        </div>
        <div className="text-lg font-bold">{g.cooperado_nome}</div>
        <div className="flex flex-wrap gap-x-4 gap-y-0.5 mt-1">
          {g.cooperado_cpf && <span className="ficha-mono text-xs text-[#555]">CPF · <strong className="text-[#111]">{g.cooperado_cpf}</strong></span>}
          {g.community_nome && <span className="ficha-mono text-xs text-[#555]">Comunidade · {g.community_nome}</span>}
          {g.cooperado_funcao && <span className="ficha-mono text-xs text-[#555]">Função · {g.cooperado_funcao}</span>}
        </div>
      </div>
      <table className="w-full text-sm border-collapse mb-5">
        <thead>
          <tr className="bg-[#0A1F14] text-white">
            <th className="text-left p-2 ficha-mono text-[10px] uppercase">Código</th>
            <th className="text-left p-2 ficha-mono text-[10px] uppercase">Produto</th>
            <th className="text-right p-2 ficha-mono text-[10px] uppercase">Qtd</th>
            <th className="text-right p-2 ficha-mono text-[10px] uppercase">Vlr Un</th>
            <th className="text-right p-2 ficha-mono text-[10px] uppercase">Total</th>
          </tr>
        </thead>
        <tbody>
          {(g.itens || []).map((it, i) => (
            <tr key={it.id || i} className={i % 2 ? "bg-[#FAFAFA]" : ""}>
              <td className="p-2 ficha-mono text-xs">{it.product_codigo}</td>
              <td className="p-2">{it.product_nome}</td>
              <td className="p-2 text-right ficha-mono">{it.quantidade}</td>
              <td className="p-2 text-right ficha-mono">{fmtBRL(it.valor_unitario)}</td>
              <td className="p-2 text-right ficha-mono font-bold">{fmtBRL(it.valor_total)}</td>
            </tr>
          ))}
        </tbody>
        <tfoot>
          <tr className="border-t-2 border-[#111]">
            <td colSpan={4} className="p-2 text-right ficha-mono text-[10px] uppercase font-bold">Total geral</td>
            <td className="p-2 text-right ficha-h text-lg">{fmtBRL(g.valor_total)}</td>
          </tr>
        </tfoot>
      </table>
      <div className="grid grid-cols-3 gap-3 border-t-2 border-[#111] pt-3 mb-6">
        <Row label="Data" value={fmtDate(g.data_compra)} mono />
        <Row label="Recibo / NF" value={g.nota_fiscal} mono />
        <Row label="Responsável" value={g.responsavel_nome} />
        <div className="col-span-2"></div>
        <div className="text-right">
          <div className="ficha-mono text-[10px] uppercase text-[#FF4F00]">VALOR PAGO</div>
          <div className="ficha-h text-3xl">{fmtBRL(g.valor_total)}</div>
        </div>
      </div>
      {g.observacoes && <div className="mb-6"><div className="ficha-mono text-[10px] uppercase text-[#555] mb-1">Observações</div><div className="border border-[#111] p-2 text-sm">{g.observacoes}</div></div>}
      <div className="grid grid-cols-2 gap-8">
        <SignBox label="Almoxarife COOMFLONA" />
        <SignBox label={`${isCom ? "Comunitário" : "Cooperado"} (Recebi o valor acima)`} />
      </div>
    </div>
  );
}


function FichaCompraFornecedorLote({ g }) {
  return (
    <div>
      <div className="flex items-center justify-between mb-4">
        <h1 className="ficha-h text-3xl">Compra de Fornecedor</h1>
        <div className="text-right">
          <div className="ficha-mono text-[10px] uppercase text-[#555]">Número</div>
          <div className="ficha-mono font-bold text-2xl">{g.numero}</div>
        </div>
      </div>
      <div className="border border-[#111] p-3 mb-5">
        <div className="ficha-mono text-[10px] uppercase tracking-widest text-[#555]">Fornecedor</div>
        <div className="text-lg font-bold">{g.supplier_nome}</div>
        {g.supplier_cnpj && <div className="ficha-mono text-xs text-[#555]">CNPJ/CPF · <strong className="text-[#111]">{g.supplier_cnpj}</strong></div>}
      </div>
      <table className="w-full text-sm border-collapse mb-5">
        <thead>
          <tr className="bg-[#0A1F14] text-white">
            <th className="text-left p-2 ficha-mono text-[10px] uppercase">Código</th>
            <th className="text-left p-2 ficha-mono text-[10px] uppercase">Produto</th>
            <th className="text-right p-2 ficha-mono text-[10px] uppercase">Qtd</th>
            <th className="text-right p-2 ficha-mono text-[10px] uppercase">Vlr Un</th>
            <th className="text-right p-2 ficha-mono text-[10px] uppercase">Total</th>
          </tr>
        </thead>
        <tbody>
          {(g.itens || []).map((it, i) => (
            <tr key={it.id || i} className={i % 2 ? "bg-[#FAFAFA]" : ""}>
              <td className="p-2 ficha-mono text-xs">{it.product_codigo}</td>
              <td className="p-2">{it.product_nome}</td>
              <td className="p-2 text-right ficha-mono">{it.quantidade}</td>
              <td className="p-2 text-right ficha-mono">{fmtBRL(it.valor_unitario)}</td>
              <td className="p-2 text-right ficha-mono font-bold">{fmtBRL(it.valor_total)}</td>
            </tr>
          ))}
        </tbody>
        <tfoot>
          <tr className="border-t-2 border-[#111]">
            <td colSpan={4} className="p-2 text-right ficha-mono text-[10px] uppercase font-bold">Total geral</td>
            <td className="p-2 text-right ficha-h text-lg">{fmtBRL(g.valor_total)}</td>
          </tr>
        </tfoot>
      </table>
      <div className="grid grid-cols-3 gap-3 border-t-2 border-[#111] pt-3 mb-6">
        <Row label="Data" value={fmtDate(g.data_compra)} mono />
        <Row label="Nota Fiscal" value={g.nota_fiscal} mono />
        <Row label="Forma de Pagamento" value={g.forma_pagamento} />
      </div>
      {g.observacoes && <div className="mb-6"><div className="ficha-mono text-[10px] uppercase text-[#555] mb-1">Observações</div><div className="border border-[#111] p-2 text-sm">{g.observacoes}</div></div>}
      <div className="grid grid-cols-2 gap-8">
        <SignBox label="Almoxarife COOMFLONA" />
        <SignBox label="Fornecedor / Entregador" />
      </div>
    </div>
  );
}


function FichaEpi({ e }) {
  return (
    <div>
      <div className="flex items-center justify-between mb-4">
        <h1 className="ficha-h text-3xl">Ficha de Entrega de EPI</h1>
        <div className="text-right">
          <div className="ficha-mono text-[10px] uppercase text-[#555]">Data</div>
          <div className="ficha-mono font-bold">{fmtDate(e.data_entrega)}</div>
        </div>
      </div>
      <div className="border border-[#111] p-3 mb-5 grid grid-cols-2 gap-3">
        <Row label={e.person_tipo === "cooperado" ? "Cooperado" : "Colaborador"} value={e.collaborator_nome} />
        <Row label="Setor / Comunidade" value={e.sector_nome} />
        <Row label="EPI" value={e.product_nome} />
        <Row label="Código" value={e.product_codigo} mono />
        <Row label="CA do Produto" value={e.product_ca} mono />
        <Row label="CA Individual (entrega)" value={e.ca_individual || e.product_ca} mono />
        <Row label="Quantidade" value={e.quantidade} mono />
        <Row label="Validade da entrega" value={fmtDateOnly(e.data_validade)} mono />
        <Row label="Responsável" value={e.responsavel_nome} />
      </div>
      <div className="border-2 border-[#111] p-4 mb-6 bg-[#FAFAFA]">
        <div className="ficha-mono text-[10px] uppercase tracking-widest text-[#555] mb-2">Declaração — NR-6</div>
        <p className="text-sm leading-relaxed">
          Declaro ter recebido o(s) Equipamento(s) de Proteção Individual (EPI) acima discriminado(s), em perfeitas condições de uso,
          juntamente com as orientações quanto à sua utilização correta, guarda, conservação e higienização, bem como sobre a
          obrigatoriedade de seu uso durante toda jornada de trabalho.
          Comprometo-me a usá-lo de forma adequada e a devolvê-lo quando solicitado ou em caso de desligamento.
        </p>
      </div>
      {e.observacoes && <div className="mb-6"><div className="ficha-mono text-[10px] uppercase text-[#555] mb-1">Observações</div><div className="border border-[#111] p-2 text-sm">{e.observacoes}</div></div>}
      <div className="grid grid-cols-2 gap-8">
        <SignBox label="Almoxarife / Entregue por" />
        <SignBox label={`${e.person_tipo === "cooperado" ? "Cooperado" : "Colaborador"} (Recebido)`} />
      </div>
    </div>
  );
}

function FichaTicket({ t }) {
  return (
    <div className="max-w-2xl mx-auto">
      <div className="text-center mb-6">
        <div className="ficha-mono text-[10px] uppercase tracking-[0.3em] text-[#FF4F00]">Ticket de Refeição</div>
        <div className="ficha-h text-4xl mt-1">{t.numero}</div>
      </div>
      <div className="border-4 border-[#0A1F14] p-6 mb-4">
        <div className="text-center border-b-2 border-dashed border-[#111] pb-4 mb-4">
          <div className="ficha-mono text-[10px] uppercase tracking-widest text-[#555]">Refeição</div>
          <div className="ficha-h text-3xl mt-1">{REFEICAO_LABEL[t.tipo_refeicao]}</div>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <Row label="Visitante" value={t.visitante_nome} />
          <Row label="Documento" value={t.visitante_documento} mono />
          <Row label="Empresa / Origem" value={t.empresa} />
          <Row label="Função / Motivo da visita" value={t.motivo_visita} />
          <Row label="Data" value={fmtDate(t.data_refeicao)} mono />
          <Row label="Emitido por" value={t.responsavel_nome} />
        </div>
        <div className="text-center mt-6 pt-4 border-t-2 border-[#111]">
          <div className="ficha-mono text-[10px] uppercase tracking-widest text-[#555]">Valor</div>
          <div className="ficha-h text-4xl text-[#FF4F00]">{fmtBRL(t.valor)}</div>
        </div>
      </div>
      {t.observacoes && <div className="mb-4"><div className="ficha-mono text-[10px] uppercase text-[#555] mb-1">Observações</div><div className="border border-[#111] p-2 text-sm">{t.observacoes}</div></div>}
      <div className="grid grid-cols-2 gap-8 mt-8">
        <SignBox label="Emissor (COOMFLONA)" />
        <SignBox label="Visitante" />
      </div>
      <div className="mt-8 text-center ficha-mono text-[9px] uppercase tracking-widest text-[#777]">
        Apresente este ticket no refeitório · Ticket {t.numero}
      </div>
    </div>
  );
}

function FichaColaborador({ d }) {
  const c = d.collaborator, epis = d.epis || [];
  return (
    <div>
      <h1 className="ficha-h text-3xl mb-1">Ficha Individual de EPIs</h1>
      <div className="ficha-mono text-[10px] uppercase text-[#555] mb-4">Histórico completo de entregas</div>
      <div className="border border-[#111] p-3 mb-5 grid grid-cols-2 gap-3">
        <Row label="Colaborador" value={c.nome} />
        <Row label="Matrícula" value={c.matricula} mono />
        <Row label="CPF" value={c.cpf} mono />
        <Row label="Setor" value={c.sector_nome} />
        <Row label="Função" value={c.funcao} />
        <Row label="Telefone" value={c.telefone} mono />
      </div>
      <table className="w-full text-sm border-collapse mb-6">
        <thead><tr className="bg-[#0A1F14] text-white"><th className="text-left p-2 ficha-mono text-[10px] uppercase">Data</th><th className="text-left p-2 ficha-mono text-[10px] uppercase">EPI</th><th className="text-left p-2 ficha-mono text-[10px] uppercase">CA</th><th className="text-right p-2 ficha-mono text-[10px] uppercase">Qtd</th><th className="text-left p-2 ficha-mono text-[10px] uppercase">Validade</th><th className="text-left p-2 ficha-mono text-[10px] uppercase">Status</th><th className="text-left p-2 ficha-mono text-[10px] uppercase">Assinatura</th></tr></thead>
        <tbody>
          {epis.length === 0 && <tr><td colSpan={7} className="p-3 text-center text-[#777] ficha-mono text-xs">Nenhuma entrega registrada</td></tr>}
          {epis.map((e, i) => (
            <tr key={i} className="border-b border-[#EEE]">
              <td className="p-2 ficha-mono text-xs">{fmtDateOnly(e.data_entrega)}</td>
              <td className="p-2">{e.product_nome} <span className="ficha-mono text-xs text-[#777]">({e.product_codigo})</span></td>
              <td className="p-2 ficha-mono text-xs">{e.ca_individual || e.product_ca || "—"}</td>
              <td className="p-2 text-right ficha-mono">{e.quantidade}</td>
              <td className="p-2 ficha-mono text-xs">{fmtDateOnly(e.data_validade)}</td>
              <td className="p-2 text-xs uppercase">{e.status}</td>
              <td className="p-2" style={{ minWidth: 100, borderBottom: "1px solid #111" }}></td>
            </tr>
          ))}
        </tbody>
      </table>
      <div className="grid grid-cols-2 gap-8 mt-12">
        <SignBox label="Responsável SST" />
        <SignBox label="Colaborador (Confere)" />
      </div>
    </div>
  );
}

function FichaCombustivel({ f }) {
  return (
    <div>
      <div className="flex items-center justify-between mb-4">
        <div>
          <div className="ficha-h text-3xl leading-tight">Requisição de Combustível</div>
          <div className="ficha-mono text-[10px] uppercase text-[#555] mt-1">Comprovante de Aprovação</div>
        </div>
        <div className="text-right">
          <div className="ficha-mono text-[10px] uppercase text-[#555]">Protocolo</div>
          <div className="ficha-h text-2xl">{f.protocolo}</div>
          <div className={`mt-1 inline-block px-2 py-0.5 text-[10px] uppercase font-bold tracking-widest ${f.status === "aprovado" ? "bg-[#1A7F37] text-white" : f.status === "utilizado" ? "bg-[#3D6B57] text-white" : "bg-[#FFC107]"}`}>{f.status}</div>
        </div>
      </div>

      <div className="grid grid-cols-2 gap-3 border-2 border-[#111] p-3 mb-4 text-sm">
        <Row label="Motorista" value={f.motorista_nome} />
        <Row label="Documento" value={f.motorista_documento || "—"} />
        <Row label="Veículo" value={f.veiculo || "—"} />
        <Row label="Placa" value={f.placa} mono />
        <Row label="Combustível" value={COMBUSTIVEL_LABEL[f.tipo_combustivel] || f.tipo_combustivel} />
        <Row label="Quantidade Autorizada" value={`${f.quantidade} L`} mono />
        <Row label="Hodômetro" value={`${f.hodometro_atual} km`} mono />
        <Row label="Unidade" value={f.filial === "km117" ? "KM 117 Anambé" : (f.filial === "cupari" ? "Rio Cuparí" : "—")} />
        <Row label="Solicitada em" value={fmtDate(f.created_at)} mono />
        <Row label="Aprovada em" value={f.approved_at ? fmtDate(f.approved_at) : "—"} mono />
        <Row label="Aprovador" value={f.approved_by_name || "—"} />
        <Row label="Expira em" value={fmtDate(f.expires_at)} mono />
      </div>

      {f.justificativa && (
        <div className="mb-3">
          <div className="ficha-mono text-[10px] uppercase text-[#555] mb-1">Justificativa</div>
          <div className="border border-[#111] p-2 text-sm">{f.justificativa}</div>
        </div>
      )}

      {f.observacoes_aprovador && (
        <div className="mb-3">
          <div className="ficha-mono text-[10px] uppercase text-[#555] mb-1">Observações do Aprovador</div>
          <div className="border border-[#111] p-2 text-sm">{f.observacoes_aprovador}</div>
        </div>
      )}

      {f.approval_token && (f.status === "aprovado" || f.status === "utilizado") && (
        <div className="border-2 border-[#3D6B57] p-4 text-center my-4">
          <div className="ficha-h text-sm mb-2">QR Code de Validação</div>
          <img src={`${API}/public/fuel/${f.approval_token}/qr.png`} alt="QR" style={{ maxWidth: 180, margin: "0 auto" }} />
          <div className="ficha-mono text-[9px] text-[#555] mt-2 break-all">Token: {f.approval_token}</div>
        </div>
      )}

      <div className="grid grid-cols-2 gap-8 mt-8">
        <SignBox label="Tesoureiro · Carpegeanne Pantoja" />
        <SignBox label="Motorista (Recebimento)" />
      </div>
      <div className="mt-6 text-center ficha-mono text-[9px] uppercase tracking-widest text-[#777]">
        COOMFLONA · CNPJ 07.315.165/0001-00 · Documento gerado eletronicamente
      </div>
    </div>
  );
}

